import { useEffect, useState } from "react";
import { UserPlus, X, Loader2, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DivisionPicker } from "@/components/DivisionPicker";

const field = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";
const EMPTY = { name: "", fargo: "", phone: "", email: "", division: "", company: "" };

// Optional `open` / `onOpenChange` let a parent (e.g. the poster viewer) open the form.
export function TournamentSignup({ eventId, title, open: openProp, onOpenChange, badge, divisions = [], onRegistered }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { message, waitlisted }
  const [divInfo, setDivInfo] = useState({}); // division name -> { spots_left, fargo_max }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) { setForm(EMPTY); setResult(null); } }, [open]);
  useEffect(() => {
    if (!open || !divisions.length) return;
    api.get(`/tournaments/${eventId}/spots`)
      .then((r) => setDivInfo(Object.fromEntries((r.data.divisions || []).map((d) => [d.name, d]))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);
  const openModal = () => setOpen(true);
  const close = () => { if (!busy) setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.includes("@")) {
      toast.error("Please enter your name, phone, and a valid email.");
      return;
    }
    if (divisions.length && !divisions.includes(form.division)) {
      toast.error("Please choose your division.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${eventId}/register`, form);
      setResult(data);
      onRegistered?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Registration failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid={`signup-btn-${eventId}`}
        className="mt-4 inline-flex flex-wrap items-center justify-center gap-1.5 bg-[#E11D2A] text-white rounded-md px-4 py-2.5 text-sm font-bold hover:bg-[#c41320] transition-colors self-start"
      >
        <UserPlus className="h-4 w-4" /> Sign Up
        {badge && (
          <span data-testid={`signup-spots-${eventId}`} className="ml-1 rounded-full bg-white text-[#E11D2A] px-2 py-0.5 text-xs font-extrabold">{badge}</span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[75] bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="signup-modal">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <h3 className="font-heading font-bold text-lg inline-flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-[#E11D2A]" /> Tournament Registration
              </h3>
              <button onClick={close} data-testid="signup-close" className="text-[#999999] hover:text-[#111111]"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5">
              {result ? (
                <div className="text-center py-6 space-y-4" data-testid="signup-success">
                  {result.waitlisted ? (
                    <Clock className="h-12 w-12 text-[#1E63C4] mx-auto" />
                  ) : (
                    <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
                  )}
                  <p className="text-[#333333] leading-relaxed" data-testid="signup-success-message">{result.message}</p>
                  <button onClick={() => setOpen(false)} data-testid="signup-done-btn" className="inline-flex items-center justify-center bg-[#1E63C4] text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-[#174e9c]">Close</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3.5">
                  <p className="text-sm text-[#333333] leading-relaxed">To register for the <strong>&apos;{title}&apos;</strong> please complete the following.</p>
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden="true" />
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Name <span className="text-[#E11D2A]">*</span></label>
                    <input className={field} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="signup-name" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">FargoRate # <span className="text-[#888888] text-xs">(optional)</span></label>
                    <input className={field} value={form.fargo} onChange={(e) => set("fargo", e.target.value)} data-testid="signup-fargo" placeholder="e.g. 525" />
                  </div>
                  {divisions.length > 0 && (
                    <DivisionPicker divisions={divisions} info={divInfo} value={form.division} onChange={(d) => set("division", d)} fargo={form.fargo} />
                  )}
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Phone # <span className="text-[#E11D2A]">*</span></label>
                    <input className={field} type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="signup-phone" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Email <span className="text-[#E11D2A]">*</span></label>
                    <input className={field} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="signup-email" required />
                  </div>
                  <button type="submit" disabled={busy} data-testid="signup-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#E11D2A] text-white rounded-md px-4 py-2.5 font-semibold hover:bg-[#c41320] disabled:opacity-60">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : "Submit"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
